import { createContext, useContext, type ReactNode } from 'react'
import { useChat, type UseChatResult } from './useChat'

/**
 * Lifts `useChat` above the router so the chat state — messages, thread id,
 * status, in-flight SSE stream — survives every route change inside the authenticated shell. ChatView
 * reads from this context instead of calling the hook itself; navigating
 * away and back is now an O(0) state transition, not a re-init.
 *
 * **Scope deliberately limited to the tab's lifetime.** This is in-
 * memory only; closing the tab still loses the conversation. We
 * explicitly avoid `localStorage` / `sessionStorage` because the
 * earlier persistence attempt only persisted `threadId` (not the
 * messages list) and produced a context-vs-UI asymmetry that led to
 * hallucinations — see the comment block in `useChat.ts`.
 *
 * **Where this gets mounted.** Inside `AuthenticatedShell` in
 * `App.tsx`, *above* the `<Switch>`. That means the provider is only
 * alive when authState is `authenticated` AND the user has the Reader
 * role — exactly when the chat surface is available.
 */

const ChatContext = createContext<UseChatResult | null>(null)

export function ChatProvider({ children }: { children: ReactNode }) {
  // Single call to useChat for the whole authenticated session. It keeps
  // running while the user is on other routes, so an SSE stream kicked off on /chat can finish writing
  // its answer while the user is browsing Initiatives — they'll see
  // the completed bubble when they navigate back.
  const chat = useChat()
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>
}

export function useChatContext(): UseChatResult {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error(
      'useChatContext must be used inside a <ChatProvider>. ' +
        'Mount ChatProvider above your routes in App.tsx.',
    )
  }
  return ctx
}

/**
 * A conversation is "active" — and chat focus mode is on — once the thread
 * has a message OR a run is in flight (`status !== idle`). The status clause
 * matters: `send()` flips status to `sending` synchronously, before the user
 * bubble commits, so arriving on `/chat` from a home/other-page ask renders
 * already-focused with no hero flash. Single source of truth for both the
 * hero collapse (ChatView) and the footer collapse (App shell).
 */
export function chatIsActive(chat: Pick<UseChatResult, 'messages' | 'status'>): boolean {
  return chat.messages.length > 0 || chat.status.kind !== 'idle'
}
